/**
 * Lightweight drawer + modal controller (replaces Flowbite).
 *
 * State is expressed ONLY through the `data-open` attribute on each
 * [data-overlay] container; all visuals live in CSS. This file never
 * adds/removes Tailwind utility classes.
 *
 * Markup contract:
 *   Trigger:   [data-overlay-open="<id>"]
 *   Close:     [data-overlay-close]  (closes nearest [data-overlay])
 *              [data-overlay-close="<id>"]  (closes that overlay)
 *   Container: <div id="<id>" data-overlay="drawer|modal"
 *                   data-placement="right|left|bottom" role="dialog" ...>
 *   Static:    add [data-overlay-static] to block backdrop-click close.
 *
 * Public API: window.RasasiOverlay = { open, close, toggle, closeTop, closeAll }
 * Events on document: rasasi:overlay:open / rasasi:overlay:close → detail {id, el}
 */
(function () {
  'use strict';

  var OPEN = 'data-open';
  var stack = [];        // open overlay ids, topmost last
  var lastFocus = {};    // id -> element to restore focus to
  var backdrop = null;

  function byId(id) { return id ? document.getElementById(id) : null; }
  function isOpen(el) { return !!el && el.hasAttribute(OPEN); }

  function ensureBackdrop() {
    if (backdrop) return backdrop;
    backdrop = document.createElement('div');
    backdrop.id = 'overlay-backdrop';
    document.body.appendChild(backdrop);
    backdrop.addEventListener('click', function () {
      var topId = stack[stack.length - 1];
      var topEl = byId(topId);
      if (topEl && topEl.hasAttribute('data-overlay-static')) return;
      if (topId) close(topId);
    });
    return backdrop;
  }

  function focusableIn(el) {
    return Array.prototype.slice.call(el.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]),' +
      ' textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ));
  }

  function syncRoot() {
    var root = document.documentElement;
    if (stack.length) root.setAttribute('data-overlay-open', '');
    else root.removeAttribute('data-overlay-open');
    var b = ensureBackdrop();
    if (stack.length) b.setAttribute(OPEN, ''); else b.removeAttribute(OPEN);
  }

  function setExpanded(id, on) {
    document.querySelectorAll('[data-overlay-open="' + id + '"]').forEach(function (t) {
      t.setAttribute('aria-expanded', on ? 'true' : 'false');
    });
  }

  function open(id) {
    var el = byId(id);
    if (!el || isOpen(el)) return;
    lastFocus[id] = document.activeElement;
    el.removeAttribute('aria-hidden');
    el.setAttribute(OPEN, '');
    if (stack.indexOf(id) === -1) stack.push(id);
    syncRoot();
    setExpanded(id, true);
    bindCloses(el);
    var f = focusableIn(el);
    (f[0] || el).focus();
    document.dispatchEvent(new CustomEvent('rasasi:overlay:open', { detail: { id: id, el: el } }));
  }

  function close(id) {
    var el = byId(id);
    if (!el || !isOpen(el)) return;
    el.removeAttribute(OPEN);
    el.setAttribute('aria-hidden', 'true');
    var i = stack.indexOf(id);
    if (i !== -1) stack.splice(i, 1);
    syncRoot();
    setExpanded(id, false);
    var restore = lastFocus[id];
    if (restore && typeof restore.focus === 'function') restore.focus();
    delete lastFocus[id];
    document.dispatchEvent(new CustomEvent('rasasi:overlay:close', { detail: { id: id, el: el } }));
  }

  function toggle(id) { var el = byId(id); if (el) { isOpen(el) ? close(id) : open(id); } }
  function closeTop() { if (stack.length) close(stack[stack.length - 1]); }
  function closeAll() { stack.slice().reverse().forEach(close); }

  // Close a given close-button: prefer its explicit id, else the overlay it lives in.
  function closeFrom(btn) {
    var id = btn.getAttribute('data-overlay-close');
    if (id) { close(id); return; }
    var host = btn.closest('[data-overlay]');
    if (host && host.id) close(host.id);
  }

  function handleCloseClick(e) {
    e.preventDefault();
    closeFrom(e.currentTarget);
  }

  // Bind the close action DIRECTLY on every [data-overlay-close] element.
  // A listener on the element itself always fires for a real click on it,
  // regardless of any ancestor stopPropagation or delegation quirks.
  // Idempotent: each button is bound at most once.
  function bindCloses(root) {
    if (!root || !root.querySelectorAll) return;
    root.querySelectorAll('[data-overlay-close]').forEach(function (btn) {
      if (btn.__rasasiCloseBound) return;
      btn.__rasasiCloseBound = true;
      btn.addEventListener('click', handleCloseClick);
    });
  }

  // Capture phase: fires before any bubble-phase stopPropagation (apps,
  // sliders, etc.) inside an overlay can swallow the click. e.target.closest
  // still resolves the clicked trigger/close element correctly.
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var opener = e.target.closest('[data-overlay-open]');
    if (opener) {
      if (opener.tagName === 'A') e.preventDefault();
      open(opener.getAttribute('data-overlay-open'));
      return;
    }
    var closer = e.target.closest('[data-overlay-close]');
    if (closer) {
      if (closer.tagName === 'A') e.preventDefault();
      var id = closer.getAttribute('data-overlay-close');
      if (id) { close(id); return; }
      var host = closer.closest('[data-overlay]');
      if (host && host.id) close(host.id);
    }
  }, true);

  document.addEventListener('keydown', function (e) {
    if (!stack.length) return;
    if (e.key === 'Escape') { closeTop(); return; }
    if (e.key === 'Tab') {
      var el = byId(stack[stack.length - 1]);
      if (!el) return;
      var f = focusableIn(el);
      if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  });

  function initContainers() {
    document.querySelectorAll('[data-overlay]:not([data-open])').forEach(function (el) {
      el.setAttribute('aria-hidden', 'true');
    });
    bindCloses(document);
  }

  // The quick-cart drawer re-renders its inner HTML (new close button) on cart
  // changes — re-bind the freshly injected close buttons.
  document.addEventListener('cart:changed', function () { bindCloses(document); });

  window.RasasiOverlay = { open: open, close: close, toggle: toggle, closeTop: closeTop, closeAll: closeAll };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initContainers);
  else initContainers();
})();
